/**
 * The signed-in state, as the UI sees it.
 *
 * Imports only from `src/data`. No component in this app touches the Firebase
 * SDK — that rule is what keeps the vendor decision reversible, and it is
 * easier to keep than to restore.
 */

import { useEffect, useState } from 'react';

import {
  refreshClaims,
  watchSession,
  type Session,
} from './data/auth.js';
import { createClaimChase } from './claimChase.js';
import {
  getMembership,
  watchMembership,
  type Membership,
} from './data/members.js';

export type Phase =
  /** Still working out whether anyone is signed in. */
  | { readonly kind: 'loading' }
  /** Nobody is signed in. */
  | { readonly kind: 'signedOut' }
  /** Signed in, but the account is on no event. */
  | { readonly kind: 'noEvent'; readonly session: Session }
  /** Signed in and a member, whether or not they have said where they are. */
  | { readonly kind: 'member'; readonly session: Session; readonly membership: Membership };

export function useSession(): Phase {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    let stopSession: (() => void) | undefined;
    let stopMembership: (() => void) | undefined;

    // BUG-1: a forced refresh fires onIdTokenChanged, which lands back in this
    // handler. Refreshing straight from the empty-claim branch was therefore an
    // unbounded loop. The chase keeps the one useful refresh and bounds the
    // rest; see claimChase.ts.
    const chase = createClaimChase({
      refresh: () => {
        void refreshClaims();
      },
    });

    void (async () => {
      stopSession = await watchSession((session) => {
        stopMembership?.();
        stopMembership = undefined;

        if (cancelled) return;

        if (session === null) {
          chase.reset();
          setPhase({ kind: 'signedOut' });
          return;
        }

        // The claim is written by a Cloud Function moments after the member
        // document appears, so a first-ever sign-in can arrive with an empty
        // one. Worth chasing a few times — never forever.
        const first = session.events[0];
        if (first === undefined) {
          setPhase({ kind: 'noEvent', session });
          chase.missing(session.uid);
          return;
        }

        chase.reset();

        // One event for now. Several is a picker, and Stage A's problem.
        void getMembership(first, session.uid).then((membership) => {
          if (cancelled || membership === null) return;
          setPhase({ kind: 'member', session, membership });
        });

        void watchMembership(first, session.uid, (membership) => {
          if (cancelled || membership === null) return;
          setPhase({ kind: 'member', session, membership });
        }).then((stop) => {
          if (cancelled) stop();
          else stopMembership = stop;
        });
      });

      if (cancelled) stopSession();
    })();

    return () => {
      cancelled = true;
      chase.stop();
      stopSession?.();
      stopMembership?.();
    };
  }, []);

  return phase;
}
